import { toast } from 'components/ui/toast-store'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import CleanupDetailSheet from './CleanupDetailSheet'
import {
	Search,
	ArrowUpDown,
	ChevronUp,
	ChevronDown,
	CheckCircle2,
	Circle,
	RefreshCw,
	Loader2,
	Trash2,
} from 'lucide-react'
import { Button } from 'components/ui/button'
import { Badge } from 'components/ui/badge'
import { Checkbox } from 'components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from 'components/ui/dialog'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/ui/table'
import { TablePagination } from 'components/ui/pagination'
import { cleanupService } from 'services'

interface UnusedComponent {
	componentName: string
	filePath: string
	absolutePath: string
	libraryImports: string[]
	internalImports: string[]
	hasCNVersion: boolean
	cnVersionPath?: string | null
	fileSize: number
	lastModified: string
}

const headerCells = [
	{ id: 'componentName', label: 'Component', sortable: true },
	{ id: 'filePath', label: 'Path', sortable: true },
	{ id: 'fileSize', label: 'Size', sortable: true, width: 'w-24' },
	{ id: 'lastModified', label: 'Modified', sortable: true, width: 'w-36' },
	{ id: 'libraryImports', label: 'Libraries', sortable: false, width: 'w-24' },
	{ id: 'hasCNVersion', label: 'CN?', sortable: true, width: 'w-16' },
]

function formatFileSize(bytes: number): string {
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
	return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	})
}

const CleanupCN = () => {
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [results, setResults] = useState<UnusedComponent[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [isDeleting, setIsDeleting] = useState(false)
	const [selectedComponent, setSelectedComponent] = useState<UnusedComponent | null>(null)
	const [sheetOpen, setSheetOpen] = useState(false)

	const [orderBy, setOrderBy] = useState<string | null>(null)
	const [order, setOrder] = useState<'asc' | 'desc'>('asc')
	const [page, setPage] = useState(0)
	const [rowsPerPage, setRowsPerPage] = useState(25)

	const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set())
	const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false)

	const searchInputRef = useRef<HTMLInputElement>(null)
	const [searchValue, setSearchValue] = useState('')
	const [filterType, setFilterType] = useState<'all' | 'has-cn' | 'mui' | 'styled'>('all')

	// Notifications go through the shared toast store (MYD-561), titled as before.
	const showToast = ({ message, variant }: { message: string; variant: 'success' | 'destructive' }) =>
		variant === 'success' ? toast.success(message, { title: 'Success' }) : toast.error(message, { title: 'Error' })

	const service = useMemo(() => cleanupService(), [])

	const runAudit = useCallback(async () => {
		setIsLoading(true)
		try {
			const data = await service.auditComponents()
			setResults(data)
			setPage(0)
		} catch (err: any) {
			showToast({ message: err?.message || 'Audit failed', variant: 'destructive' })
		} finally {
			setIsLoading(false)
		}
	}, [service])

	useEffect(() => {
		runAudit()
	}, [runAudit])

	const handleSort = (columnId: string) => {
		if (orderBy === columnId) {
			setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
		} else {
			setOrderBy(columnId)
			setOrder('asc')
		}
	}

	const handleRowClick = (component: UnusedComponent) => {
		setSelectedComponent(component)
		setSheetOpen(true)
	}

	const handleDelete = async (filePath: string) => {
		setIsDeleting(true)
		try {
			await service.deleteComponent(filePath)
			setResults((prev) => prev.filter((c) => c.filePath !== filePath))
			setSheetOpen(false)
			setSelectedComponent(null)
			showToast({ message: `Deleted ${filePath}`, variant: 'success' })
		} catch (err: any) {
			showToast({ message: err?.message || 'Delete failed', variant: 'destructive' })
		} finally {
			setIsDeleting(false)
		}
	}

	const handleBulkDelete = async () => {
		if (selectedPaths.size === 0) return
		setIsDeleting(true)
		setBulkDeleteConfirmOpen(false)
		try {
			const result = await service.deleteComponents([...selectedPaths])
			const deletedSet = new Set(result.deleted as string[])
			setResults((prev) => prev.filter((c) => !deletedSet.has(c.filePath)))
			setSelectedPaths(new Set())
			const msg = result.errors?.length
				? `Deleted ${result.deleted.length}, ${result.errors.length} failed`
				: `Deleted ${result.deleted.length} components`
			showToast({ message: msg, variant: result.errors?.length ? 'destructive' : 'success' })
		} catch (err: any) {
			showToast({ message: err?.message || 'Bulk delete failed', variant: 'destructive' })
		} finally {
			setIsDeleting(false)
		}
	}

	const toggleSelected = (filePath: string) => {
		setSelectedPaths((prev) => {
			const next = new Set(prev)
			if (next.has(filePath)) next.delete(filePath)
			else next.add(filePath)
			return next
		})
	}

	const filteredRecords = useMemo(() => {
		let filtered = results

		// Filter by type
		if (filterType === 'has-cn') {
			filtered = filtered.filter((c) => c.hasCNVersion)
		} else if (filterType === 'mui') {
			filtered = filtered.filter((c) => c.libraryImports.some((lib) => lib.startsWith('@mui')))
		} else if (filterType === 'styled') {
			filtered = filtered.filter((c) => c.libraryImports.includes('styled-components'))
		}

		// Filter by search
		if (searchValue) {
			const lower = searchValue.toLowerCase()
			filtered = filtered.filter(
				(c) =>
					c.componentName.toLowerCase().includes(lower) ||
					c.filePath.toLowerCase().includes(lower) ||
					c.libraryImports.some((lib) => lib.toLowerCase().includes(lower)),
			)
		}

		return filtered
	}, [results, searchValue, filterType])

	const sortedRecords = useMemo(() => {
		if (!orderBy) return filteredRecords
		return [...filteredRecords].sort((a: any, b: any) => {
			let aVal = a[orderBy]
			let bVal = b[orderBy]

			// Handle numeric sorting for fileSize
			if (orderBy === 'fileSize') {
				return order === 'asc' ? aVal - bVal : bVal - aVal
			}

			// Handle boolean sorting for hasCNVersion
			if (orderBy === 'hasCNVersion') {
				return order === 'asc' ? (aVal === bVal ? 0 : aVal ? -1 : 1) : aVal === bVal ? 0 : aVal ? 1 : -1
			}

			aVal = (aVal || '').toString().toLowerCase()
			bVal = (bVal || '').toString().toLowerCase()
			if (aVal < bVal) return order === 'asc' ? -1 : 1
			if (aVal > bVal) return order === 'asc' ? 1 : -1
			return 0
		})
	}, [filteredRecords, orderBy, order])

	const paginatedRecords = useMemo(() => {
		const start = page * rowsPerPage
		return sortedRecords.slice(start, start + rowsPerPage)
	}, [sortedRecords, page, rowsPerPage])

	// Select all is scoped to currently filtered records
	const filteredPaths = useMemo(() => filteredRecords.map((c) => c.filePath), [filteredRecords])
	const allFilteredSelected = filteredPaths.length > 0 && filteredPaths.every((fp) => selectedPaths.has(fp))
	const someFilteredSelected = filteredPaths.some((fp) => selectedPaths.has(fp))

	const toggleSelectAll = () => {
		if (allFilteredSelected) {
			// Deselect all filtered
			setSelectedPaths((prev) => {
				const next = new Set(prev)
				for (const fp of filteredPaths) next.delete(fp)
				return next
			})
		} else {
			// Select all filtered
			setSelectedPaths((prev) => {
				const next = new Set(prev)
				for (const fp of filteredPaths) next.add(fp)
				return next
			})
		}
	}

	const SortIcon = ({ columnId }: { columnId: string }) => {
		if (orderBy !== columnId) return <ArrowUpDown className="w-3 h-3 ml-1 text-gray-400" />
		return order === 'asc' ? (
			<ChevronUp className="w-3 h-3 ml-1 text-[#0080fe]" />
		) : (
			<ChevronDown className="w-3 h-3 ml-1 text-[#0080fe]" />
		)
	}

	const filterButtons: { key: typeof filterType; label: string }[] = [
		{ key: 'all', label: 'All' },
		{ key: 'has-cn', label: 'Has CN' },
		{ key: 'mui', label: 'Uses MUI' },
		{ key: 'styled', label: 'Styled Components' },
	]

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						{/* Title + count */}
						<div className="flex items-center justify-between mb-4">
							<div className="flex items-center gap-3">
								<h2 className="text-2xl font-semibold text-gray-800">Component Cleanup</h2>
								{!isLoading && (
									<Badge variant="secondary" className="text-sm">
										{filteredRecords.length} unused
									</Badge>
								)}
							</div>
							<div className="flex items-center gap-2">
								{selectedPaths.size > 0 && (
									<Button variant="danger" onClick={() => setBulkDeleteConfirmOpen(true)} disabled={isDeleting}>
										<Trash2 className="w-4 h-4 mr-1" />
										Delete {selectedPaths.size} Selected
									</Button>
								)}
								<Button onClick={runAudit} disabled={isLoading}>
									{isLoading ? (
										<Loader2 className="w-4 h-4 mr-1 animate-spin" />
									) : (
										<RefreshCw className="w-4 h-4 mr-1" />
									)}
									{isLoading ? 'Scanning...' : 'Run Audit'}
								</Button>
							</div>
						</div>

						{/* Toolbar: search + filters */}
						<div className="flex flex-wrap items-center gap-3 mb-4">
							<div className="relative flex-1 min-w-[200px] max-w-sm">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
								<input
									ref={searchInputRef}
									type="text"
									placeholder="Search components..."
									value={searchValue}
									onChange={(e) => {
										setSearchValue(e.target.value)
										setPage(0)
									}}
									className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0080fe]/30 focus:border-[#0080fe]"
								/>
							</div>
							<div className="flex gap-1">
								{filterButtons.map((fb) => (
									<button
										key={fb.key}
										onClick={() => {
											setFilterType(fb.key)
											setPage(0)
										}}
										className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
											filterType === fb.key
												? 'bg-[#0080fe] text-white'
												: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
										}`}
									>
										{fb.label}
									</button>
								))}
							</div>
						</div>

						{/* Table */}
						{isLoading ? (
							<div className="flex items-center justify-center py-20">
								<Loader2 className="w-8 h-8 animate-spin text-[#0080fe]" />
								<span className="ml-3 text-gray-500">Scanning codebase...</span>
							</div>
						) : paginatedRecords.length === 0 ? (
							<div className="text-center py-20 text-gray-400">
								{results.length === 0 ? 'No unused components found.' : 'No results match your filter.'}
							</div>
						) : (
							<>
								<div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
									<Table className="table-fixed w-full">
										<TableHeader>
											<TableRow className="border-b-2 border-[#0080fe]">
												<TableHead className="w-10">
													<Checkbox
														checked={allFilteredSelected}
														onCheckedChange={toggleSelectAll}
														className={someFilteredSelected && !allFilteredSelected ? 'opacity-60' : ''}
													/>
												</TableHead>
												{headerCells.map((cell) => (
													<TableHead
														key={cell.id}
														className={`text-xs font-semibold text-gray-600 uppercase tracking-wider ${cell.width || ''} ${
															cell.sortable !== false ? 'cursor-pointer select-none hover:text-gray-900' : ''
														}`}
														onClick={() => cell.sortable !== false && handleSort(cell.id)}
													>
														<span className="inline-flex items-center">
															{cell.label}
															{cell.sortable !== false && <SortIcon columnId={cell.id} />}
														</span>
													</TableHead>
												))}
											</TableRow>
										</TableHeader>
										<TableBody>
											{paginatedRecords.map((comp) => (
												<TableRow
													key={comp.filePath}
													className="h-9 hover:bg-[#fffbf2] cursor-pointer transition-colors"
													onClick={() => handleRowClick(comp)}
												>
													<TableCell className="py-1.5 w-10" onClick={(e) => e.stopPropagation()}>
														<Checkbox
															checked={selectedPaths.has(comp.filePath)}
															onCheckedChange={() => toggleSelected(comp.filePath)}
														/>
													</TableCell>
													<TableCell className="py-1.5 text-sm font-medium text-gray-900 truncate">
														{comp.componentName}
													</TableCell>
													<TableCell className="py-1.5 text-xs text-gray-500 font-mono truncate">
														{comp.filePath}
													</TableCell>
													<TableCell className="py-1.5 text-xs text-gray-500 w-24">
														{formatFileSize(comp.fileSize)}
													</TableCell>
													<TableCell className="py-1.5 text-xs text-gray-500 w-36">
														{formatDate(comp.lastModified)}
													</TableCell>
													<TableCell className="py-1.5 w-24">
														{comp.libraryImports.length > 0 && (
															<Badge variant="secondary" className="text-[10px]">
																{comp.libraryImports.length}
															</Badge>
														)}
													</TableCell>
													<TableCell className="py-1.5 w-16">
														{comp.hasCNVersion ? (
															<CheckCircle2 className="w-4 h-4 text-green-500" />
														) : (
															<Circle className="w-3.5 h-3.5 text-gray-300" />
														)}
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</div>

								<TablePagination
									page={page}
									rowsPerPage={rowsPerPage}
									count={filteredRecords.length}
									onPageChange={setPage}
									onRowsPerPageChange={setRowsPerPage}
								/>
							</>
						)}
					</main>
				</div>
			</div>

			{/* Detail Sheet */}
			<CleanupDetailSheet
				open={sheetOpen}
				onClose={() => {
					setSheetOpen(false)
					setSelectedComponent(null)
				}}
				component={selectedComponent}
				onDelete={handleDelete}
				isDeleting={isDeleting}
			/>

			{/* Bulk Delete Confirmation */}
			<Dialog open={bulkDeleteConfirmOpen} onClose={() => setBulkDeleteConfirmOpen(false)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Delete {selectedPaths.size} Components</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete {selectedPaths.size} selected component{selectedPaths.size !== 1 ? 's' : ''}? This cannot be undone.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setBulkDeleteConfirmOpen(false)}>
							Cancel
						</Button>
						<Button variant="danger" onClick={handleBulkDelete}>
							<Trash2 className="w-4 h-4 mr-1" />
							Delete All
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

		</div>
	)
}

export default CleanupCN
